import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

export interface NotificationSettings {
  user_id: number;
  activity_follow_up_enabled: boolean;
  low_balance_enabled: boolean;
  weekly_event_enabled: boolean;
}

export interface NotificationSettingsUpdate {
  activity_follow_up_enabled?: boolean;
  low_balance_enabled?: boolean;
  weekly_event_enabled?: boolean;
}

const notificationKeys = {
  all: ['notifications'] as const,
  settings: () => [...notificationKeys.all, 'settings'] as const,
};

// Get notification settings
export const useNotificationSettings = () => {
  return useQuery({
    queryKey: notificationKeys.settings(),
    queryFn: async () => {
      const response = await api.get<NotificationSettings>('/notifications/settings');
      return response.data;
    },
  });
};

// Update notification settings
export const useUpdateNotificationSettings = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settings: NotificationSettingsUpdate) => {
      const response = await api.put<NotificationSettings>('/notifications/settings', settings);
      return response.data;
    },
    onMutate: async (newSettings) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationKeys.settings() });
      
      // Snapshot the previous value
      const previousSettings = queryClient.getQueryData<NotificationSettings>(notificationKeys.settings());
      
      // Optimistically update to the new value
      if (previousSettings) {
        queryClient.setQueryData<NotificationSettings>(notificationKeys.settings(), {
          ...previousSettings,
          ...newSettings,
        });
      }
      
      // Return a context object with the snapshotted value
      return { previousSettings };
    },
    onError: (err, newSettings, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousSettings) {
        queryClient.setQueryData(notificationKeys.settings(), context.previousSettings);
      }
    },
    onSettled: () => {
      // Optionally refetch in the background to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: notificationKeys.settings() });
    },
  });
};
